import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, CircularProgress, Breadcrumbs, Link, Alert, Snackbar, Button,
  TextField, Autocomplete,
} from '@mui/material';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import AppsIcon from '@mui/icons-material/Apps';
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined';
import { useAuth } from '../../contexts/AuthContext';
import {
  getApplicationsByOwner,
  getApplicationSummary,
  getGridData,
  getReviewerOptions,
  addReviewerToApplication,
  getApplicationOwners,
  removeApplicationOwner,
} from '../../services/api';
import AppSummaryCard from './AppSummaryCard';
import UserAccessGrid from './UserAccessGrid';
import FinalSignOffBar from './FinalSignOffBar';

export default function DashboardPage() {
  const { user } = useAuth();

  const [applications, setApplications] = useState([]);
  const [appsLoading, setAppsLoading] = useState(false);
  const [selectedAppId, setSelectedAppId] = useState('');
  const [reviewers, setReviewers] = useState([]);
  const [reviewersLoading, setReviewersLoading] = useState(false);
  const [selectedReviewer, setSelectedReviewer] = useState(null);
  const [addReviewerLoading, setAddReviewerLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [owners, setOwners] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadProgress, setLoadProgress] = useState('');
  const [error, setError] = useState('');
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState('error');
  const [toastOpen, setToastOpen] = useState(false);

  // ── Load the owner's applications on mount ─────────────────────────────────
  useEffect(() => {
    let active = true;
    setAppsLoading(true);
    (async () => {
      try {
        const apps = await getApplicationsByOwner(user.userId);
        if (active) setApplications(apps);
      } catch (err) {
        if (active) {
          setError(`Failed to load applications: ${err.message}`);
          setToastSeverity('error');
          setToastMessage(`Failed to load applications: ${err.message}`);
          setToastOpen(true);
        }
      } finally {
        if (active) setAppsLoading(false);
      }
    })();
    return () => { active = false; };
  }, [user.userId, user.email]);

  // ── Load summary + grid whenever the selected app changes ─────────────────
  const loadAppData = useCallback(async (appId) => {
    if (!appId) {
      setSummary(null);
      setUsers([]);
      setOwners([]);
      setReviewers([]);
      setSelectedReviewer(null);
      return;
    }

    // Immediately clear stale data so old app's data is never visible
    setSummary(null);
    setUsers([]);
    setOwners([]);
    setLoading(true);
    setLoadProgress('');
    setError('');

    try {
      // Fire summary + grid + owners in parallel
      const [sum, accessList, ownerList] = await Promise.all([
        getApplicationSummary(appId),
        getGridData(appId, (loaded, total) => {
          setLoadProgress(`Loading records… ${loaded} / ${total}`);
        }),
        getApplicationOwners(appId),
      ]);
      setSummary(sum);
      setUsers(accessList);
      setOwners(ownerList);
    } catch (err) {
      setError(`Failed to load application data: ${err.message}`);
      setToastSeverity('error');
      setToastMessage(`Failed to load application data: ${err.message}`);
      setToastOpen(true);
    } finally {
      setLoading(false);
      setLoadProgress('');
    }
  }, []);

  // Trigger loadAppData every time the dropdown value changes
  useEffect(() => {
    loadAppData(selectedAppId);
  }, [selectedAppId, loadAppData]);

  useEffect(() => {
    let active = true;

    if (!selectedAppId) {
      setReviewers([]);
      setSelectedReviewer(null);
      setReviewersLoading(false);
      return () => { active = false; };
    }

    setReviewersLoading(true);
    setSelectedReviewer(null);

    (async () => {
      try {
        const reviewerList = await getReviewerOptions(selectedAppId);
        if (active) setReviewers(reviewerList);
      } catch (err) {
        if (active) {
          setReviewers([]);
          setError(`Failed to load reviewers: ${err.message}`);
          setToastSeverity('error');
          setToastMessage(`Failed to load reviewers: ${err.message}`);
          setToastOpen(true);
        }
      } finally {
        if (active) setReviewersLoading(false);
      }
    })();

    return () => { active = false; };
  }, [selectedAppId]);

  const handleAppChange = (e) => {
    setSelectedAppId(e.target.value);
  };

  const handleRefresh = () => loadAppData(selectedAppId);

  const handleAddReviewer = async () => {
    if (!selectedAppId || !selectedReviewer?.userId) return;

    setAddReviewerLoading(true);
    try {
      await addReviewerToApplication(selectedAppId, selectedReviewer.userId, user.userId);
      setReviewers((prev) => prev.filter((reviewer) => reviewer.userId !== selectedReviewer.userId));
      setToastSeverity('success');
      setToastMessage(`Reviewer ${selectedReviewer.name || selectedReviewer.userId} added successfully`);
      setToastOpen(true);
      setSelectedReviewer(null);
    } catch (err) {
      setError(`Failed to add reviewer: ${err.message}`);
      setToastSeverity('error');
      setToastMessage(`Failed to add reviewer: ${err.message}`);
      setToastOpen(true);
    } finally {
      setAddReviewerLoading(false);
    }
  };

  const handleRemoveOwner = async (ownerUserId) => {
    try {
      await removeApplicationOwner(selectedAppId, ownerUserId);
      setOwners((prev) => prev.filter((o) => o.userid !== ownerUserId));
      setToastSeverity('success');
      setToastMessage('Owner removed successfully');
      setToastOpen(true);
    } catch (err) {
      setToastSeverity('error');
      setToastMessage(`Failed to remove owner: ${err.message}`);
      setToastOpen(true);
    }
  };

  // ── Local update helpers (avoid full reload after retain/revoke) ────────────
  const handleUsersUpdate = useCallback((updatedUsers) => {
    setUsers(updatedUsers);
    // Recalculate summary counts from updated users
    setSummary((prev) => {
      if (!prev) return prev;
      const pending = updatedUsers.filter((u) => u.reviewStatus === 'Pending').length;
      const retained = updatedUsers.filter((u) => u.reviewStatus === 'Retain').length;
      const revoked = updatedUsers.filter((u) => u.reviewStatus === 'Revoke').length;
      return { ...prev, totalUsers: updatedUsers.length, pending, retained, revoked };
    });
  }, []);

  const selectedApp = applications.find((a) => a.appId === selectedAppId);

  // ── Calculate total auth events from grid data (sum of loginCount) ─────────
  const totalAuthEvents = useMemo(
    () => users.reduce((sum, u) => sum + (Number(u.loginCount) || 0), 0),
    [users]
  );

  const selectorFieldSx = {
    bgcolor: '#fff',
    borderRadius: 1,
    '& .MuiOutlinedInput-root': {
      borderRadius: 1,
      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#1A2027' },
      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#1A2027' },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#1A2027 !important',
        borderWidth: '1px',
      },
    },
  };

  return (
    <Box>
      {/* ── Breadcrumbs ─────────────────────────────────────────────────── */}
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
        <Link underline="hover" color="inherit" href="#"
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <HomeOutlinedIcon sx={{ fontSize: 16 }} /> Dashboard
        </Link>
        {selectedApp && (
          <Typography variant="body2" color="text.primary" fontWeight={600}>
            {selectedApp.appName}
          </Typography>
        )}
      </Breadcrumbs>

      {/* ── Page header ─────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ color: '#0A1929', mb: 0.5 }}>
            Access Review
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Review and certify user access for your applications, <strong>{user.name}</strong>.
          </Typography>
        </Box>

        {/* ── Application dropdown ─────────────────────────────────────── */}
        <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 1.5, flexWrap: 'wrap' }}>
          <Autocomplete
            size="small"
            options={applications}
            value={applications.find((a) => a.appId === selectedAppId) || null}
            onChange={(_, newValue) => setSelectedAppId(newValue?.appId || '')}
            getOptionLabel={(app) => app.appName || ''}
            isOptionEqualToValue={(option, value) => option.appId === value.appId}
            disabled={appsLoading}
            loading={appsLoading}
            sx={{ minWidth: 300 }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Application"
                placeholder="Type to search…"
                sx={selectorFieldSx}
              />
            )}
            renderOption={(props, app) => (
              <Box component="li" {...props} key={app.appId}>
                <AppsIcon sx={{ fontSize: 15, color: '#1976D2', mr: 1, flexShrink: 0 }} />
                {app.appName}
              </Box>
            )}
          />
        </Box>
      </Box>

      {/* ── Loading spinner ──────────────────────────────────────────────── */}
      {loading && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 10, gap: 2 }}>
          <CircularProgress size={36} sx={{ color: '#1976D2' }} />
          <Typography variant="body2" color="text.secondary">
            {loadProgress || 'Loading application data…'}
          </Typography>
        </Box>
      )}

      {/* ── App Content ─────────────────────────────────────────────────── */}
      {!loading && summary && (
        <>
          <FinalSignOffBar
            appId={selectedAppId}
            appName={summary.appName}
            isSignedOff={summary.isSignedOff}
            signoffBy={summary.signoffBy}
            signoffAt={summary.signoffAt}
            pendingCount={summary.pending}
            dueDate={summary.dueDate}
            onSignOff={handleRefresh}
          />
          <AppSummaryCard
            summary={summary}
            totalAuthEvents={totalAuthEvents}
            owners={owners}
            onRemoveOwner={handleRemoveOwner}
            reviewers={reviewers}
            reviewersLoading={reviewersLoading}
            selectedReviewer={selectedReviewer}
            onReviewerChange={setSelectedReviewer}
            onAddReviewer={handleAddReviewer}
            addReviewerLoading={addReviewerLoading}
          />
          <UserAccessGrid
            appId={selectedAppId}
            appName={summary.appName}
            accessPeriod={summary.accessPeriod}
            users={users}
            isSignedOff={summary.isSignedOff}
            onDataChange={handleRefresh}
            onUsersUpdate={handleUsersUpdate}
          />
        </>
      )}

      {/* ── Empty state ─────────────────────────────────────────────────── */}
      {!loading && !selectedAppId && (
        <Box sx={{
          textAlign: 'center', py: 12,
          bgcolor: '#fff', borderRadius: 2, border: '1px dashed #D0D7DE',
        }}>
          <PeopleIcon sx={{ fontSize: 48, color: '#D0D7DE', mb: 2 }} />
          {applications.length === 0 ? <>
            <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500 }}>
              No applications found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              You are not assigned to any applications.
            </Typography>
          </> : <>
            <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500 }}>
              Select an application to begin the access review
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Use the dropdown above to choose one of your assigned applications.
            </Typography></>}

        </Box>
      )}

      {/* ── Error toast ─────────────────────────────────────────────────── */}
      <Snackbar
        open={toastOpen}
        autoHideDuration={5000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={toastSeverity} variant="filled" onClose={() => setToastOpen(false)} sx={{ fontSize: '0.8rem' }}>
          {toastMessage || error}
        </Alert>
      </Snackbar>
    </Box>
  );
}

function PeopleIcon(props) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em" {...props}>
      <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
    </svg>
  );
}
